import { ValidatorFn, Validators } from '@angular/forms';

export interface FieldValidationResult {
    validators: ValidatorFn[];
    messages: { [key: string]: string };
}

const LETTERS_ONLY_VALIDATOR: ValidatorFn = (control) => {
    if (!control.value) return null;
    return /^[a-zA-Z\s]+$/.test(control.value) ? null : { pattern: true };
};

const PARAMETERIZABLE_RULES = [
    'min', 'max', 'between', 'in', 'not_in', 'mimes',
    'date_format', 'after', 'before', 'size', 'digits',
    'digits_between', 'regex',
];

export function buildFieldValidators(field: {
    type: string;
    label?: string;
    name: string;
    required?: boolean;
    validation?: { rules?: string[] };
}): FieldValidationResult {
    const validators: ValidatorFn[] = [];
    const messages: { [key: string]: string } = {};
    const rules: string[] = field.validation?.rules || [];
    const label = field.label || field.name;

    if (field.required) {
        validators.push(Validators.required);
        messages['required'] = `${label} is required.`;
    }

    for (const rule of rules) {
        const colonIndex = rule.indexOf(':');
        const key = colonIndex !== -1 ? rule.substring(0, colonIndex) : rule;
        const param = colonIndex !== -1 ? rule.substring(colonIndex + 1) : '';

        switch (key) {
            case 'string':
                if (field.type !== 'tel' && !rules.some(r => r.startsWith('regex:'))) {
                    validators.push(LETTERS_ONLY_VALIDATOR);
                    messages['pattern'] = `${label} must contain only letters and spaces.`;
                }
                break;
            case 'regex': {
                const match = param.match(/^\/(.+)\/([gimsuy]*)$/);
                if (match) {
                    validators.push(Validators.pattern(new RegExp(match[1], match[2])));
                    messages['pattern'] = `Please enter a valid ${label}.`;
                }
                break;
            }
            case 'max':
                if (param) {
                    if (field.type === 'number') { validators.push(Validators.max(+param)); messages['max'] = `${label} must not exceed ${param}.`; }
                    else { validators.push(Validators.maxLength(+param)); messages['maxlength'] = `Maximum ${param} characters allowed.`; }
                }
                break;
            case 'min':
                if (param) {
                    if (field.type === 'number') { validators.push(Validators.min(+param)); messages['min'] = `${label} must be at least ${param}.`; }
                    else { validators.push(Validators.minLength(+param)); messages['minlength'] = `Minimum ${param} characters required.`; }
                }
                break;
        }
    }

    if (field.type === 'tel') {
        validators.push(Validators.minLength(10), Validators.maxLength(10));
        messages['minlength'] = 'Phone number must be 10 digits.';
        messages['maxlength'] = 'Phone number must be 10 digits.';
    }

    return { validators, messages };
}

export function getFieldErrorMessage(
    errors: { [key: string]: any },
    messages: { [key: string]: string },
    label: string
): string | null {
    if (errors['required']) return messages['required'] || `${label} is required.`;
    if (errors['invalidEmailFormat'] || errors['invalidTLD']) return messages['invalidEmailFormat'] || 'Please enter a valid email address.';
    if (errors['min']) return messages['min'] || `Minimum value is ${errors['min'].min}.`;
    if (errors['max']) return messages['max'] || `Maximum value is ${errors['max'].max}.`;
    if (errors['minlength']) return messages['minlength'] || `Minimum ${errors['minlength'].requiredLength} characters required.`;
    if (errors['maxlength']) return messages['maxlength'] || `Maximum ${errors['maxlength'].requiredLength} characters allowed.`;
    if (errors['pattern']) return messages['pattern'] || `${label} is invalid.`;
    const firstKey = Object.keys(errors)[0];
    return messages[firstKey] || `${label} is invalid.`;
}

export function getMaxLengthFromRules(rules: string[], fieldType?: string): number {
    if (fieldType === 'tel') return 10;
    const maxRule = rules?.find(r => r.startsWith('max:'));
    return maxRule ? +maxRule.split(':')[1] : 524288;
}

export function getMinLengthFromRules(rules: string[], fieldType?: string): number | null {
    if (fieldType === 'tel') return 10;
    const minRule = rules?.find(r => r.startsWith('min:'));
    return minRule ? +minRule.split(':')[1] : null;
}

export { PARAMETERIZABLE_RULES };
